/* eslint-disable @typescript-eslint/no-loop-func */
import { translatedDates } from '@/constants/date-mode-languages';
import { DataCalculationMethodEnum, DateDisplayMode } from '@/constants/enum';
import store from '@/redux/store';
import { IParamsApi } from '@/types/apis/params';
import dayjs from 'dayjs';
import moment from 'moment';

export function validateTime(time: string) {
  const timeReg = /^([0-9]|0[0-9]|1[0-9]|2[0-3]):[0-5][0-9]$/;
  return time.match(timeReg);
}

/**
 * Compare two time strings in HH:mm format
 * @param time1 First time string in HH:mm format (e.g. "13:30")
 * @param time2 Second time string in HH:mm format (e.g. "14:45")
 * @returns {number}
 * -2 if time format is invalid
 *
 * -1 if time1 is earlier than time2
 *
 * 0 if time1 equals time2
 *
 * 1 if time1 is later than time2
 */
export const compareTime = (time1: string, time2: string): number => {
  // Validate time format
  if (!validateTime(time1) || !validateTime(time2)) {
    return -2;
  }

  // Split hours and minutes
  const [hours1, minutes1] = time1.split(':').map(Number);
  const [hours2, minutes2] = time2.split(':').map(Number);

  // Convert to minutes for comparison
  const totalMinutes1 = hours1 * 60 + minutes1;
  const totalMinutes2 = hours2 * 60 + minutes2;

  // Return comparison result
  // Returns -1 if time1 is earlier than time2
  // Returns 0 if times are equal
  // Returns 1 if time1 is later than time2
  return totalMinutes1 < totalMinutes2 ? -1 : totalMinutes1 > totalMinutes2 ? 1 : 0;
};

export const getSomeDaysAgo = (day: number): Date => {
  const date = new Date();
  date.setDate(date.getDate() - day);
  date.setHours(0, 0, 0);
  return date;
};
export const formatDate = (date: number | Date | string, format?: string) => {
  return moment(typeof date === 'number' ? date * 1000 : date).format(format ? format : 'D MMM YYYY, h:mm a');
};

const orderedDay = (weekWorkingDays: IParamsApi.IWeekWorkingDay[], preOrderDate?: Date): Date => {
  const settings = store.getState().settings.dataSettings;

  const timezoneOffset = settings.date_timezone_offset;
  let today = createDateByTimezoneOffset(timezoneOffset, preOrderDate);
  if (isDateAfterCutoffTime(today, weekWorkingDays)) {
    today = increaseDateTimeByDays(today, 1);
  }
  return today;
};

export const handleCalculateDeliveryTime = (preparationDay: number, estimatedDay: number) => {
  const { date_format: format, date_locale: locale } = store.getState().settings.dataSettings;
  const numberPrepareDay = moment().add(preparationDay);
  const startDate = numberPrepareDay.locale(locale).format(format);
  const endDate = moment(numberPrepareDay).add(estimatedDay, 'd').locale(locale).format(format);
  return {
    startDate: startDate,
    endDate: endDate,
  };
};

export const formatDateOption = (formatType: string, lang: string) => {
  return moment().locale(lang).format(formatType);
};

const parseTimezone = (rawTimezone: string): string => {
  if (!rawTimezone) {
    return '';
  }
  // Extract the timezone from the raw timezone string
  const timezone = rawTimezone.split(':')[0];
  return timezone;
};
export const createDateByTimezoneOffset = (timezoneOffset?: string, currentDay?: Date): Date => {
  if (timezoneOffset === 'Default customer timezone' || !timezoneOffset) {
    return currentDay ? new Date(currentDay) : new Date();
  }
  const date = dayjs(currentDay || new Date());
  const timezone = parseTimezone(timezoneOffset);
  const timezoneDate = date.tz(timezone).format('YYYY-MM-DD HH:mm:ss');
  return new Date(timezoneDate);
};

export const formatDateYYYYMMDD = (time: Date): string => {
  const day = time.getDate().toString().padStart(2, '0');
  const month = (time.getMonth() + 1).toString().padStart(2, '0'); // Adding 1 to month to account for zero-based index
  const year = time.getFullYear();
  return `${year}-${month}-${day}`;
};

export const isDateAfterCutoffTime = (date: Date, workingDays: IParamsApi.IWeekWorkingDay[]) => {
  const today = date.getDay();
  const currentHour = date.getHours();
  const currentMinute = date.getMinutes();
  const weekDay = workingDays.find((item) => item.day === today);
  const condition = weekDay?.cut_off_after.split(':');
  let conditionHour;
  let conditionMinute;
  if (condition) {
    conditionHour = Number(condition[0]);
    conditionMinute = Number(condition[1]);
  }

  if (
    !weekDay?.enable ||
    (conditionHour !== undefined && currentHour > conditionHour) ||
    (currentHour === conditionHour && conditionMinute && currentMinute >= conditionMinute)
  ) {
    return true;
  } else {
    return false;
  }
};

export const increaseDateTimeByDays = (currentDay: Date, daysToAdd: number): Date => {
  const increasedDate = new Date(currentDay.getTime()); // Clone the date to avoid mutating the original
  increasedDate.setDate(increasedDate.getDate() + daysToAdd);
  return increasedDate;
};

export const calculateNextDay = (orderDay: Date, increaseDays: number, workingDays: IParamsApi.IWeekWorkingDay[]) => {
  const dataSettings = store.getState().settings.dataSettings;
  const dayOff = dataSettings.specific_day_off.map((item) => new Date(item)).map((day) => formatDateYYYYMMDD(day));

  let nextDay = new Date(orderDay);
  let formattedDay = formatDateYYYYMMDD(nextDay);
  const isHasWorkday = workingDays.find((day) => day.enable === 1);

  if (isHasWorkday) {
    let count = 0;
    for (let i = 0; i < 365; i++) {
      // eslint-disable-next-line no-loop-func
      const weekDay = workingDays.find((day) => day.day === nextDay.getDay());
      // eslint-disable-next-line no-loop-func
      const isSpecDay = dayOff.find((item) => item === formattedDay);
      if (weekDay && weekDay?.enable === 1 && !isSpecDay) {
        if (count >= increaseDays) break;
        count = count + 1;
      }
      formattedDay = formatDateYYYYMMDD(increaseDateTimeByDays(nextDay, 1));
      nextDay = increaseDateTimeByDays(nextDay, 1);
    }
  }

  return nextDay;
};

export const calculateEstimatedDays = (
  orderToShipDays: number,
  prepDays: number,
  shipToReceiveDays: number,
  estDays: number,
  preOrderDate?: Date,
) => {
  const dataSettings = store.getState().settings.dataSettings;
  const { isUseSeparateWorkingDays, dateCalculationMethod } = store.getState().settings.dataSettings;
  const weekWorkingDaysForPrepare = isUseSeparateWorkingDays
    ? dataSettings.week_working_days.prepare
    : dataSettings.week_working_days.prepareAndDelivery;
  const weekWorkingDaysForDelivery = isUseSeparateWorkingDays
    ? dataSettings.week_working_days.delivery
    : dataSettings.week_working_days.prepareAndDelivery;
  let orderPrepareDay = orderedDay(weekWorkingDaysForPrepare, preOrderDate);
  let orderDeliveryDay = orderedDay(weekWorkingDaysForDelivery, preOrderDate);

  const startShippedDay = calculateNextDay(orderPrepareDay, orderToShipDays, weekWorkingDaysForPrepare);
  const endShippedDay = calculateNextDay(orderPrepareDay, prepDays, weekWorkingDaysForPrepare);
  const startDeliveryDay = calculateNextDay(
    dateCalculationMethod === DataCalculationMethodEnum.OrderDate ? orderDeliveryDay : endShippedDay,
    shipToReceiveDays,
    weekWorkingDaysForDelivery,
  );
  const endDeliveryDay = calculateNextDay(
    dateCalculationMethod === DataCalculationMethodEnum.OrderDate ? orderDeliveryDay : endShippedDay,
    estDays,
    weekWorkingDaysForDelivery,
  );

  return {
    startShippedDay,
    endShippedDay,
    startDeliveryDay,
    endDeliveryDay,
  };
};

export function renderRelativeDate(date: Date): string {
  const givenDate = new Date(date);
  const dateLocale = store.getState().settings.dataSettings.date_locale;
  const previewLanguage = store.getState().preview.selectedLanguage;
  const locale = previewLanguage || dateLocale;
  const today = new Date();
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);

  // Set time to 00:00:00 for both today and tomorrow to compare only the dates
  today.setHours(0, 0, 0, 0);
  tomorrow.setHours(0, 0, 0, 0);

  const translatedLocaleExist = translatedDates.find((translatedLocale) => translatedLocale.locale === locale);

  if (givenDate.setHours(0, 0, 0, 0) === today.getTime()) {
    return translatedLocaleExist ? translatedLocaleExist.today : 'Today';
  } else if (givenDate.setHours(0, 0, 0, 0) === tomorrow.getTime()) {
    return translatedLocaleExist ? translatedLocaleExist.tomorrow : 'Tomorrow';
  } else {
    return renderDate(date);
  }
}

export const renderDate = (date: Date) => {
  const { date_locale, date_format } = store.getState().settings.dataSettings;
  const previewLanguage = store.getState().preview.selectedLanguage;
  return `${moment(date)
    .locale(previewLanguage || date_locale)
    .format(date_format)}`;
};

export const getProcessedDate = (date: Date) => {
  const dateDisplayMode = store.getState().settings.dataSettings.date_display_mode;
  return dateDisplayMode === DateDisplayMode.Relative ? renderRelativeDate(date) : renderDate(date);
};

export const getLastMonth = () => {
  const firstDate = new Date();
  firstDate.setDate(1);
  firstDate.setMonth(firstDate.getMonth() - 1);
  const lastDate = new Date();
  lastDate.setDate(0);
  return {
    start: firstDate,
    end: lastDate,
  };
};

export const getLastSomesMonth = (month: number) => {
  const firtDate = new Date();
  firtDate.setDate(1);
  firtDate.setMonth(firtDate.getMonth() - month);
  const lastDate = new Date();
  lastDate.setMonth(lastDate.getMonth() - month + 1);
  lastDate.setDate(0);
  return {
    start: firtDate,
    end: lastDate,
  };
};

export const getArrayDateFromStartDateToEndDate = (startDate: Date, endDate: Date): string[] => {
  const dates: string[] = [];
  const currentDate = new Date(startDate);

  // Clone endDate để không ảnh hưởng đến tham số gốc
  const lastDate = new Date(endDate);

  while (currentDate <= lastDate) {
    dates.push(currentDate.toLocaleDateString('en-US'));
    currentDate.setDate(currentDate.getDate() + 1);
  }

  return dates;
};
